import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Goal } from "../types/Goal";
import { goalsKey } from "../queries/goal-queries";
import { useMsal } from "@azure/msal-react";
import { BackendAPI } from "../util/Env";

export const useCreateGoalMutation = () => {
    const { accounts } = useMsal()
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (newGoal: Goal) => {
            const response = await fetch(BackendAPI + '/goals-api.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'ownerId': accounts[0].idTokenClaims!.oid!,
                },
                body: JSON.stringify(newGoal),
            });

            if (!response.ok) {
                throw new Error(`Error creating goal: ${response.statusText}`);
            }

            return response.json();
        },
        onMutate: async (newGoal: { title: string }) => {
            await queryClient.cancelQueries({ queryKey: [goalsKey] });

            const prevGoals = queryClient.getQueryData<Goal[]>([goalsKey]);

            const tempGoal = {
                id: `temp-${Date.now()}`,
                title: newGoal.title,
            };
            queryClient.setQueryData([goalsKey], (old: Goal[] = []) => [...old, tempGoal]);

            return { prevGoals };
        },
        onError: (_, _2, context) => {
            queryClient.setQueryData([goalsKey], context?.prevGoals ?? []);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [goalsKey] });
        },
    });
}

export const useDeleteGoalMutation = () => {
    const { accounts } = useMsal()
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await fetch(`${BackendAPI}/goals-api.php`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'ownerId': accounts[0].idTokenClaims!.oid!,
                },
                body: JSON.stringify({ id }),
            });

            if (!response.ok) {
                throw new Error(`Error deleting goal: ${response.statusText}`);
            }

            return response.json();
        },
        onMutate: async (id: string) => {
            await queryClient.cancelQueries({ queryKey: [goalsKey] });

            const prevGoals = queryClient.getQueryData<Goal[]>([goalsKey]);

            queryClient.setQueryData([goalsKey], (old: Goal[] = []) => old.filter(goal => goal.id !== id));

            return { prevGoals };
        },
        onError: (_, _2, context) => {
            queryClient.setQueryData([goalsKey], context?.prevGoals ?? []);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [goalsKey] });
        },
    });
};